/**
 * Run a list of jobs a few at a time, with a way to stop.
 *
 * Used by "Generate All Variables" on the CRF Builder: 40 forms at ~138 s each
 * took ~90 minutes one after another (measured 2026-10-13, Clinical Dev). The
 * variables call writes nothing itself, so forms can run side by side.
 *
 * Rules: at most `concurrency` jobs in flight; each item is started at most
 * once; one job failing does not stop the others; `shouldStop()` is checked
 * before each new item is taken, and jobs already started run to the end.
 * Pattern follows functions/src/acrf/acrfGenerator.ts (shared cursor + workers).
 *
 * Pure: no React, no Firebase.
 */

/**
 * Why 8, and what it risks.
 *
 * Each variables call also bursts its own controlled-terminology matching
 * model calls (one per picklist, in parallel, 2 attempts each). If one of those
 * is refused for rate limiting (429 / RESOURCE_EXHAUSTED) on both attempts, the
 * server does NOT fail the form: `enrichVariablesWithCt` in
 * functions/src/crfBuilderFunctions.ts catches it and marks that variable
 * `sponsor_defined`, logging "[enrichVariablesWithCt] LLM/match error for
 * <var>; falling back to sponsor_defined". The form still reports success here.
 * So the risk of more forms at once is quietly WEAKER terminology (terms that
 * should match a CDISC codelist come back sponsor-defined), not a visible error.
 * Where it would show: that log line in the generateCRFVariablesV2 logs, and
 * more sponsor-defined picklists in the data dictionary.
 *
 * Was 4. Raised to 8 on 2026-10-24 after 10 days of Clinical Dev logs run at 4
 * showed no real 429 / RESOURCE_EXHAUSTED. 8 itself has NOT been measured yet:
 * check the logs above on a fresh build after it is deployed, and go back to 4
 * if the fallback line appears.
 */
export const CRF_VARIABLES_CONCURRENCY = 8;

export type PoolOutcome<R> =
  | { status: 'done'; value: R }
  | { status: 'failed'; error: Error }
  | { status: 'not-started' };

export async function runPool<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
  options: { shouldStop?: () => boolean } = {},
): Promise<Array<PoolOutcome<R>>> {
  const outcomes: Array<PoolOutcome<R>> = items.map(() => ({ status: 'not-started' as const }));
  let cursor = 0;
  const runWorker = async () => {
    while (cursor < items.length) {
      if (options.shouldStop?.()) return;
      const index = cursor++;
      try {
        outcomes[index] = { status: 'done', value: await worker(items[index], index) };
      } catch (err) {
        outcomes[index] = { status: 'failed', error: err instanceof Error ? err : new Error(String(err)) };
      }
    }
  };
  const workers = Math.max(1, Math.min(Math.floor(concurrency) || 1, items.length));
  await Promise.all(Array.from({ length: workers }, runWorker));
  return outcomes;
}

export function summarizeOutcomes(outcomes: ReadonlyArray<PoolOutcome<unknown>>) {
  return {
    done: outcomes.filter(o => o.status === 'done').length,
    failed: outcomes.filter(o => o.status === 'failed').length,
    notStarted: outcomes.filter(o => o.status === 'not-started').length,
  };
}
